import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { BusinessModeFieldType } from '../types';
import {
    FIELDS_TABLE_COL_NAME,
    FIELDS_TABLE_COL_TYPE,
    FIELDS_TABLE_COL_VALUES,
    FIELDS_TABLE_MIN_WIDTH,
} from './model/constants';

interface BusinessModeFieldsPanelProps {
    fields: BusinessModeFieldType[];
    isSaving: boolean;
    onSave: () => void;
    onCopyStructure: () => void;
}

const BusinessModeFieldsPanel = ({ fields, isSaving, onSave, onCopyStructure }: BusinessModeFieldsPanelProps) => {
    const { t } = useTranslation(['Settings', 'common']);

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">{t('businessMode.activeFields')}</h6>
                {fields.length > 0 ? (
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none"
                        onClick={onCopyStructure}
                    >
                        {t('common:clipboard.copy')}
                    </Button>
                ) : null}
            </div>
            {!fields.length ? (
                <p className="text-muted fs-12 mb-0">{t('businessMode.emptyFields')}</p>
            ) : (
                <Table
                    responsive
                    className="table-sm align-middle mb-0"
                    style={{ tableLayout: 'fixed', minWidth: `${FIELDS_TABLE_MIN_WIDTH}px`, width: '100%' }}
                >
                    <thead>
                        <tr className="bg-light">
                            <th style={{ width: `${FIELDS_TABLE_COL_NAME}px`, minWidth: `${FIELDS_TABLE_COL_NAME}px` }}>
                                {t('businessMode.fieldTable.name')}
                            </th>
                            <th style={{ width: `${FIELDS_TABLE_COL_TYPE}px`, minWidth: `${FIELDS_TABLE_COL_TYPE}px` }}>
                                {t('businessMode.fieldTable.type')}
                            </th>
                            <th style={{ minWidth: `${FIELDS_TABLE_COL_VALUES}px` }}>
                                {t('businessMode.fieldTable.values')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.map((field, idx) => {
                            const titleByI18n = field.i18nKey
                                ? t(`businessMode.fieldLabels.${field.i18nKey}`, { defaultValue: '' })
                                : '';
                            const fieldTitle =
                                titleByI18n ||
                                field.title ||
                                field.i18nKey ||
                                `${t('businessMode.fieldTable.field')} #${idx + 1}`;

                            return (
                                <tr key={`${field.i18nKey || field.title || 'field'}-${idx}`}>
                                    <td className="text-break">{fieldTitle}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <code>{field.type}</code>
                                    </td>
                                    <td className="text-muted text-break">{field.selectValues?.join(', ') || '-'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            <div className="mt-3 d-flex justify-content-end">
                <Button size="sm" onClick={onSave} disabled={isSaving} variant="primary">
                    {isSaving ? t('common:buttons.saving') : t('businessMode.save')}
                </Button>
            </div>
        </div>
    );
};

export default BusinessModeFieldsPanel;
